import { TextLinkButton } from '@casavo/base-ui'
import { Body } from '@casavo/habitat'
import * as Sentry from '@sentry/nextjs'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { UserAuthentication } from '@/components/auth/UserAuthentication'
import { ConceptImage } from '@/components/ConceptImage'
import { DetailGalleryButton } from '@/components/Gallery/Buttons/DetailGalleryButton'
import GalleryButtonIcon from '@/components/Gallery/Buttons/Icon'
import { DesktopImageGallery } from '@/components/Gallery/DesktopImageGallery'
import { MobileImageGallery } from '@/components/Gallery/MobileImageGallery'
import Icon from '@/components/Icon'
import { Layout } from '@/components/Layout'
import { contentLayoutInner, layoutContentInnerStretched } from '@/components/Layout/index.css'
import { ModalGalleryAndPlanimetry } from '@/components/Modal/Types/GalleryAndPlanimetry'
import LoginModalForm, { loginModalFormOptions } from '@/components/Modal/Types/LoginModalForm'
import { Translated } from '@/components/Translated'
import { VisitRequestModal } from '@/containers/PriorityDetail/sections/RequestVisit/VisitRequestForm'
import { useExpressPotentialPropertyVisitInterest } from '@/containers/PriorityDetail/useExpressPotentialPropertyVisitInterest'
import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { useCurrentTenant } from '@/context/TenantProvider'
import { Button } from '@/design-lib/components/Button'
import { ToastError } from '@/design-lib/components/Toast/ToastError'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import { getPriorityThankyouPage } from '@/i18n-routes'
import { GetPotentialPropertyVisitInterestResponse, initApi as glugluPrivateApi } from '@/lib/api-gluglu-auth'
import { TENANTS } from '@/lib/tenants/constants'
import { headerHeightDesktop } from '@/shared-styles/common-variables'
import { backgroundColor, gap } from '@/shared-styles/styles.css'
import { Potential_Property } from '@/types/cate-graphql-generated-types'
import { CasavoInventory } from '@/types/listing'
import { CardDataListing, CardDataPriority } from '@/types/property-card'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { storePropertyVisited } from '@/utils/new-property'
import { BuyerInfo } from '@/utils/priority-detail-utils'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'
import { InventoryBannerBig } from 'components/InventoryBannerBig'

import { AlreadyBookedBanner } from './AlreadyBookedBanner/AlreadyBookedBanner'
import { ButtonFeedback } from './ButtonFeedback'
import { advantages, advantagesCTA } from './data'
import { PriorityDetailInfo } from './sections/PriorityDetailInfo/PriorityDetailInfo'
import { RequestVisitCTA } from './sections/RequestVisit'
import { SimilarPropertiesSection } from './sections/SimilarProperties'
import {
  floatingCta,
  priorityDetailGalleryHeader,
  priorityDetailGalleryWrapper,
  priorityDetailPhotoCta,
} from './styles.css'
import { useVisitInterestBeforeLogin } from './useVisitInterestBeforeLogin'
import { parsePotentialPropertyMedia } from './utils'

type Props = {
  buyerInfo: BuyerInfo | null
  cities: ContextValue
  priority: Potential_Property
  similarListings: CardDataListing[]
  similarPriorities: CardDataPriority[]
}

export const PriorityDetail: React.FC<Props> = ({
  buyerInfo,
  cities,
  priority,
  similarListings,
  similarPriorities,
}) => {
  const expressVisit = useExpressPotentialPropertyVisitInterest(priority.transactionId)
  const device = useDevice()
  const tenant = useCurrentTenant()
  const { isLoading, user } = useAuthentication()
  const { closeBottomSheet, openBottomSheet } = useBottomSheet()
  const { closeModal, openModal } = useModal()
  const [isSubmitting, setSubmitting] = useState(false)
  const [showToastError, setShowToastError] = useState(false)
  const [visitInterest, setVisitInterest] = useState<GetPotentialPropertyVisitInterestResponse | undefined>(undefined)
  const { t } = useTranslation()
  const casavoInventoryBannerBigRef = useRef<HTMLDivElement>(null)
  const { floorPlan, photoGallery } = parsePotentialPropertyMedia(priority.potentialPropertyMedia)
  const planimetry = floorPlan.length > 0 ? floorPlan[0].url : ''
  const thankYouPageUrl = getPriorityThankyouPage(priority.slug, priority.city)
  const isPriorityLocked = !buyerInfo && !user && !isLoading
  const { resetVisitInterestBeforeLogin } = useVisitInterestBeforeLogin()
  const showAlreadyBookedBanner = visitInterest === GetPotentialPropertyVisitInterestResponse.INTEREST_ALREADY_EXPRESSED
  const similarProperties = [...similarPriorities, ...similarListings]
  const requestVisitCtaLabel =
    device === 'mobile'
      ? t('priority:Priority_DetailPage_FloatingBanner_CTAmobile')
      : t('priority:Priority_DetailPage_FloatingBanner_CTA')

  const [advantagesData, advantagesCTAData] = useMemo(() => [advantages(tenant), advantagesCTA(tenant)], [tenant])

  const handleClickRequestVisitCta = () =>
    user && !isLoading
      ? handleClick(GTMEventCategory.PriorityDetail, 'FloatingBanner_RequestVisit')
      : handleClick(GTMEventCategory.LockedPriorityDetail, 'RequestVisit')

  useEffect(() => {
    if (buyerInfo) {
      handleShow(GTMEventCategory.PriorityDetail, 'Access-FromMail')
    }
  }, [buyerInfo])

  const handleHideToastError = () => {
    setShowToastError(false)
    setSubmitting(false)
  }

  const handleBannerCtaClick = () => {
    handleClick(GTMEventCategory.PriorityDetail, 'InfoBanner_TellMeMore')
    const headerOffset = device === 'desktop' && headerHeightDesktop + parseInt(theme.spacings['2xl'])

    window.scrollTo({
      behavior: 'smooth',
      top: casavoInventoryBannerBigRef.current.offsetTop - headerOffset,
    })
  }

  const handleRequestVisitCtaBuyerInfo = () => {
    handleClick(GTMEventCategory.PriorityDetail, 'FloatingBanner_RequestVisit')
    switch (buyerInfo.tenant_id) {
      case TENANTS.ucsc:
        setSubmitting(true)
        return expressVisit({ args: { contactId: buyerInfo.contact_id }, tenant: buyerInfo.tenant_id })
          .then(() => {
            setShowToastError(false)
            setVisitInterest(GetPotentialPropertyVisitInterestResponse.INTEREST_ALREADY_EXPRESSED)
          })
          .catch((error) => {
            setShowToastError(true)
            Sentry.captureException(error, (scope) => {
              scope.setExtra('channel', 'lisa.priorityProperty')
              return scope
            })
          })
          .finally(() => {
            setSubmitting(false)
          })
      case TENANTS.casavo:
        return openModal(
          <VisitRequestModal
            buyerInfo={buyerInfo}
            expressVisitInterest={(args) => expressVisit({ args, tenant: buyerInfo.tenant_id })}
            setShowToastError={setShowToastError}
            thankYouPageUrl={thankYouPageUrl}
          />,
          {
            overlay: true,
          }
        )
    }
  }

  const handleLockedPriorityLogin = () => {
    resetVisitInterestBeforeLogin()

    if (device === 'mobile') {
      openBottomSheet(
        <UserAuthentication
          conceptImage={<ConceptImage height={75} name="ideas" width={75} />}
          mainTitle="Login_PriorityDetail_BottomSheet_UnlockTitle"
          returnUrl={window.location.href}
          touchPoint="FromLockedPriority"
          onLoggedIn={closeBottomSheet}
        />,
        { bgColor: 'offWhite', isPriority: true, minHeight: 610 }
      )
    } else {
      openModal(
        <LoginModalForm
          isPriority
          bgColor="offWhite"
          conceptImage={<ConceptImage height={120} name="ideas" width={120} />}
          mainTitle="Login_PriorityDetail_BottomSheet_UnlockTitle"
          returnURL={window.location.href}
          touchPoint="FromLockedPriority"
          onLoggedIn={closeModal}
        />,
        loginModalFormOptions
      )
    }
  }

  useEffect(() => {
    const fetchVisitInterest = async () => {
      try {
        const result = await glugluPrivateApi().getPotentialPropertyVisitInterest(priority.transactionId)
        setVisitInterest(result)
      } catch (error) {
        console.error('Error:', error)
      }
    }

    if (tenant === TENANTS.casavo) {
      fetchVisitInterest()
    }

    if (!user && !isLoading) {
      handleShow(GTMEventCategory.LockedPriorityDetail, 'View')
    }
  }, [user, isLoading, tenant])

  useEffect(() => {
    if (isStorageSupported(() => sessionStorage) && typeof window.sessionStorage !== 'undefined') {
      storePropertyVisited(priority.transactionId)
    }
  }, [])

  const handleUnlock = () => {
    handleClick(GTMEventCategory.LockedPriorityDetail, 'Unlock', 'FromPhotoCarousel')
    handleLockedPriorityLogin()
  }

  return (
    <div className={sprinkles({ paddingBottom: { desktop: 0, mobile: '4xl' } })}>
      <Layout contentWidth="default" showHeaderOnMobile={false} tenant={tenant}>
        <div>
          <div
            className={cn(
              priorityDetailGalleryWrapper,
              layoutContentInnerStretched,
              sprinkles({ paddingBottom: isPriorityLocked ? '4xl' : 'xl', paddingTop: 'xl' })
            )}
          >
            <div className={cn(contentLayoutInner['default'], isPriorityLocked && sprinkles({ position: 'relative' }))}>
              <div className={priorityDetailGalleryHeader}>
                {isPriorityLocked ? (
                  <div className={cn(sprinkles({ display: 'flex' }), gap.l)}>
                    <Body noMargin size="m">
                      <Translated i18Key="priority:PriorityDeatil_NotLogged_Intro_Description" />
                    </Body>
                    <TextLinkButton
                      label={t('priority:PriorityDetail_NotLogged_Intro_CTA')}
                      onClick={() => {
                        handleClick(GTMEventCategory.LockedPriorityDetail, 'Unlock', 'FromTopDescription')
                        handleLockedPriorityLogin()
                      }}
                    />
                  </div>
                ) : tenant === TENANTS.casavo ? (
                  <div className={sprinkles({ display: 'flex', gap: 's' })}>
                    <span aria-label="" role="img">
                      🏡
                    </span>
                    <Body noMargin size="m">
                      <Translated i18Key="priority:Priority_DetailPage_PreviewDescription" />
                    </Body>
                  </div>
                ) : null}
              </div>

              {device === 'mobile' ? (
                <MobileImageGallery
                  cities={cities}
                  citySlug={priority.city}
                  eventCategory={GTMEventCategory.PriorityDetail}
                  handleUnlock={isPriorityLocked && handleUnlock}
                  photos={photoGallery}
                  planimetryUrl={planimetry}
                  title={priority.title}
                />
              ) : (
                <DesktopImageGallery
                  cities={cities}
                  citySlug={priority.city}
                  eventCategory={GTMEventCategory.PriorityDetail}
                  handleUnlock={isPriorityLocked && handleUnlock}
                  id={priority.transactionId}
                  photos={photoGallery}
                  planimetryUrl={planimetry}
                  title={priority.title}
                />
              )}

              {!isPriorityLocked && planimetry && (
                <DetailGalleryButton
                  className={sprinkles({ marginLeft: { desktop: 0, mobile: 'm' }, marginTop: 'm' })}
                  Icon={() => <GalleryButtonIcon kind="floorplan" />}
                  label={t('listing-detail:planimetry')}
                  onClick={(event: React.SyntheticEvent<HTMLElement>) => {
                    handleClick(GTMEventCategory.PriorityDetail, 'Planimetry')
                    event.preventDefault()
                    openModal(
                      <ModalGalleryAndPlanimetry
                        cities={cities}
                        citySlug={priority.city}
                        closeModal={closeModal}
                        images={photoGallery}
                        openType="planimetry"
                        planimetryUrl={planimetry}
                        startIndex={0}
                        title={priority.title}
                      />
                    )
                  }}
                />
              )}
              {isPriorityLocked && (
                <button
                  className={priorityDetailPhotoCta}
                  onClick={() => {
                    handleClick(GTMEventCategory.LockedPriorityDetail, 'Unlock', 'FromPhotoCTA')
                    handleLockedPriorityLogin()
                  }}
                >
                  <Icon.Lock />
                  <Body noMargin size="s">
                    {t('priority:PriorityDetail_NotLogged_Intro_CTA')}
                  </Body>
                  <Icon.Chevron />
                </button>
              )}
            </div>
          </div>

          <PriorityDetailInfo
            advantages={advantagesCTAData}
            disableVisitCTA={isSubmitting}
            handleBannerCtaClick={handleBannerCtaClick}
            handleRequestVisitCtaBuyerInfo={handleRequestVisitCtaBuyerInfo}
            hasBuyerInfo={Boolean(buyerInfo)}
            isPriorityLocked={isPriorityLocked}
            priority={priority}
            setShowToastError={setShowToastError}
            showAlreadyBookedBanner={showAlreadyBookedBanner}
            thankYouPageUrl={getPriorityThankyouPage(priority.slug, priority.city)}
          />

          <div ref={casavoInventoryBannerBigRef} className={layoutContentInnerStretched}>
            <InventoryBannerBig
              advantages={advantagesData}
              color="brandedBackground"
              hasContentBottom={isPriorityLocked}
              inventory={CasavoInventory.Priority}
              tenant={tenant}
            />

            {isPriorityLocked && (
              <div
                className={cn(sprinkles({ paddingBottom: 'l', paddingTop: 's' }), backgroundColor.brandedBackground)}
              >
                <Button
                  center
                  label={t('priority:Priority_DetailPage_AdvantagesBox_CTA')}
                  styleVariant="secondary"
                  onClick={() => {
                    handleClick(GTMEventCategory.LockedPriorityDetail, 'Unlock', 'FromInfoBanner')
                    handleLockedPriorityLogin()
                  }}
                />
              </div>
            )}
          </div>

          {similarProperties.length > 0 && (
            <SimilarPropertiesSection similarListings={similarListings} similarPriorities={similarPriorities} />
          )}

          <div className={floatingCta}>
            {showAlreadyBookedBanner ? (
              <>
                <div className={cn(backgroundColor.greyscale50, sprinkles({ marginBottom: 'm', padding: 'm' }))}>
                  <div className={sprinkles({ paddingX: '4xl', textAlign: 'center' })}>
                    <Body noMargin color="root.neutral.400" size="xs">
                      {t('priority:PriorityDetail_FloatingBanner_Booked_Description')}
                    </Body>
                  </div>
                </div>

                <AlreadyBookedBanner className={sprinkles({ marginBottom: 'm', marginX: 'm' })} />
              </>
            ) : (
              <div className={sprinkles({ display: 'flex', gap: 's', padding: 'm' })}>
                {tenant === TENANTS.casavo && <ButtonFeedback className={sprinkles({ flex: 1 })} />}
                {buyerInfo ? (
                  <Button
                    fullWidth
                    className={sprinkles({ flex: 1 })}
                    disabled={isSubmitting}
                    label={requestVisitCtaLabel}
                    loading={user && isSubmitting}
                    minWidth={190}
                    onClick={handleRequestVisitCtaBuyerInfo}
                  />
                ) : (
                  <RequestVisitCTA
                    className={sprinkles({ flex: 1 })}
                    gtmTouchPoint="FromVisitLockedPriority"
                    handleCtaClick={handleClickRequestVisitCta}
                    setShowToastError={setShowToastError}
                    thankYouPageUrl={thankYouPageUrl}
                    transactionId={priority.transactionId}
                  />
                )}
              </div>
            )}
          </div>

          <ToastError error={showToastError} setError={handleHideToastError} />
        </div>
      </Layout>
    </div>
  )
}
